import type { FieldDefinition, FieldResponsePayload, MappedField, MappedInputType, SectionDefinition } from '@/types'
import { FIELD_RESPONSE_MAPPING, FIELD_VALUE_ALIASES } from '@/data/fieldResponseMapping'
import { matchDropdownValue } from '@/utils/matchDropdownValue'
import { OCR_CONFIDENCE_THRESHOLD } from '@/services/ocrService';

// Build flat lookup: formFieldId.toLowerCase() → { field, sectionId }
function buildFieldIndex(sections: SectionDefinition[]): Record<string, { field: FieldDefinition; sectionId: string }> {
    const index: Record<string, { field: FieldDefinition; sectionId: string }> = {}
    for (const section of sections) {
        for (const field of section.fields) {
            index[field.id.toLowerCase()] = { field, sectionId: section.id }
        }
    }
    return index
}

// Resolve a response key → form field ID: config alias first, then direct match
function resolveFormFieldId(responseKey: string): string {
    const lowerKey = responseKey.toLowerCase()
    const configEntry = Object.entries(FIELD_RESPONSE_MAPPING).find(
        ([k]) => k.toLowerCase() === lowerKey
    )
    return configEntry ? configEntry[1].toLowerCase() : lowerKey
}

function resolveInputType(fieldType: FieldDefinition['type']): MappedInputType {
    if (fieldType === 'select') return 'select'
    if (fieldType === 'toggle') return 'toggle'
    return 'text'
}

// Select: thin wrapper around matchDropdownValue for backwards compatibility
export function matchSelectValue(rawValue: string, options: string[]): string | null {
    const result = matchDropdownValue(
        rawValue,
        options.map((o) => ({ label: o })),
        OCR_CONFIDENCE_THRESHOLD
    )
    return result.matched ? result.matchedOption!.label : null
}

// Toggle: normalize to "true" / "false"
// Truthy: yes / y / true / 1 / x / ✓ / checked / selected / on (covers physical form ticks and Azure DI markers)
// Falsy:  no / n / false / 0 / unchecked / unselected / off (and anything unrecognized)
function convertToggle(rawValue: string): string {
    const lower = rawValue.toLowerCase().trim()
    return ['yes', 'y', 'true', '1', 'x', '✓', 'checked', 'selected', 'on'].includes(lower) ? 'true' : 'false'
}

/**
 * Maps an AI or OCR response payload to the matching form fields.
 *
 * - Sections in the payload are ignored; matching is done by field key across all form sections.
 * - Field key resolution: FIELD_RESPONSE_MAPPING config first, then case-insensitive direct match.
 * - Select fields: matched case-insensitively against available options; skipped if no match.
 * - Toggle fields: converted from "yes/true/1" → "true", others → "false".
 * - Null/undefined values are skipped.
 * - Fields not present in the form config are ignored.
 */
export function mapResponseToForm(
    payload: FieldResponsePayload,
    sections: SectionDefinition[]
): { mappedFields: MappedField[] } {
    const fieldIndex = buildFieldIndex(sections)
    const mappedFields: MappedField[] = []

    for (const sectionData of Object.values(payload.sections ?? {})) {
        for (const [rawKey, fieldData] of Object.entries(sectionData.fields ?? {})) {
            if (fieldData.value == null) continue

            const resolvedId = resolveFormFieldId(rawKey)
            const entry = fieldIndex[resolvedId]
            if (!entry) continue

            const { field, sectionId } = entry
            const inputType = resolveInputType(field.type)
            let resolvedValue: string
            let pendingMatch = false

            if (inputType === 'select') {
                const rawStr = String(fieldData.value)
                // Explicit alias takes precedence over the matching algorithm
                const fieldAliases = FIELD_VALUE_ALIASES[field.id] ?? {}
                const aliasedValue =
                    Object.entries(fieldAliases).find(([k]) => k.toLowerCase() === rawStr.toLowerCase())?.[1] ?? rawStr

                const options = field.options ?? []
                const hasRealOptions = options.some((o) => !o.startsWith('Select'))
                if (hasRealOptions) {
                    // Options are loaded — require a match, skip if none found
                    const match = matchSelectValue(aliasedValue, options)
                    if (!match) continue
                    resolvedValue = match
                } else {
                    // Options not yet loaded (sentinel only) — store raw value and flag
                    // so the store keeps it for re-matching once the dropdown loads
                    resolvedValue = aliasedValue
                    pendingMatch = true
                }
            } else if (inputType === 'toggle') {
                const rawStr = String(fieldData.value)
                const fieldAliases = FIELD_VALUE_ALIASES[field.id] ?? {}
                const aliasedValue =
                    Object.entries(fieldAliases).find(([k]) => k.toLowerCase() === rawStr.toLowerCase())?.[1] ?? rawStr
                resolvedValue = convertToggle(aliasedValue)
            } else {
                resolvedValue = String(fieldData.value)
            }

            mappedFields.push({
                fieldKey: field.id,  // always the form field ID, not the raw response key
                label: field.label,
                sectionId,
                value: resolvedValue,
                inputType,
                confidence: Math.round(fieldData.confidence * 100) / 100,
                source: fieldData.source,
                ...(pendingMatch ? { pendingMatch: true } : {}),
                boxValue: fieldData.boxValue ?? null,
            })
        }
    }

    return { mappedFields }
}
